<template>
    <DefaultHeader />
    <div class="container-fluid">
        <div class="row mb-2">
            <div class="col-12 d-flex flex-column vh-auto h-100">
                <MissionInfoPanel ref="panel" :mission="mission" @endCommentChange="onEndCommentChange"
                    @addComment="addMissionComment" @canceled="refreshData" />
            </div>
        </div>
        <div v-if="mission?.status !== 'FINISHED' && mission?.status !== 'CANCELED'" class="row mb-2">
            <div class="col-12 d-flex flex-column vh-auto h-100">
                <MissionLaunchSection ref="launcher" :mission="mission" :notification="notificationBanner"
                    @startMission="startMission" @endMission="endMission" @startCommentChange="onStartCommentChange"
                    @endCommentChange="onEndCommentChange" @addComment="addMissionComment" @setAlertCode="setAlertCode"
                    @refresh="refreshData" />
            </div>
        </div>
    </div>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'
import MissionInfoPanel from './Sections/MissionInfoPanel.vue'
import MissionLaunchSection from './Sections/MissionLauncherSection.vue'
import MissionService from '@/services/mission.service'
import MissionRepository from '@/repositories/mission.repo.js'

export default {
    components: {
        DefaultHeader,
        DefaultFooter,
        MissionInfoPanel,
        MissionLaunchSection
    },
    data() {
        return {
            mission: null,
            draftStartComment: "",
            draftEndComment: "",
            alertCode: false,
            missionPollTimer: null,
            isRefreshingMission: false
        };
    },
    computed: {
        /**
         * Objet { html, alertClass } pour l'alerte du panel.
         * Réagit automatiquement aux changements de `mission` et du rôle utilisateur.
         */
        notificationBanner() {
            if (!this.mission) return null;

            if (this.alertCode) {
                return {
                    html: '<strong>ATTENTION : mot de passe sous contrainte détecté</strong>. Dites au chauffeur que tout est bon et après avoir raccroché, gérez l\'alarme sur ATLANT pour appeler les forces de l\'ordre.',
                    alertClass: 'alert-danger'
                };
            }

            else if (this.mission.status === 'PENDING') {
                const hasTransporteur = !!(this.mission.transporteurId || (this.mission.transporteur && this.mission.transporteur.id));
                if (!this.$isViewer && !hasTransporteur) {
                    return {
                        html: 'Assignez un <strong>véhicule principal</strong> pour pouvoir lancer la mission.',
                        alertClass: 'alert-warning'
                    };
                }
                else if (!Array.isArray(this.mission?.members) || this.mission.members.length === 0) {
                    return {
                        html: 'Assignez un <strong>chauffeur</strong> au véhicule principal pour pouvoir lancer la mission.',
                        alertClass: 'alert-warning'
                    };
                }
                else if (!this.mission.driverVerified) {
                    return {
                        html: 'Le <strong>chauffeur n’est pas vérifié</strong>. Saisissez et <strong>validez son mot de passe</strong> avant de démarrer la mission.',
                        alertClass: 'alert-warning'
                    };
                }
                else if (this.mission.verifiedInfo === 'ONCE' && this.allPrechecksOk && !this.draftStartComment) {
                    return {
                        html: 'Prise de poste OK. Sélectionnez un <strong>commentaire de départ</strong> avant de démarrer la mission.',
                        alertClass: 'alert-success'
                    };
                }
                else if (!this.draftStartComment) {
                    return {
                        html: 'Sélectionnez un <strong>commentaire de départ</strong> avant de démarrer la mission.',
                        alertClass: 'alert-warning'
                    };
                }
                else if (!this.allPrechecksOk) {
                    return {
                        html: 'Certains <strong>tests préalables</strong> ne sont pas validés. Vous pouvez quand même lancer la mission, mais vous devrez <strong>rédiger un commentaire pour justifier ce choix</strong>.',
                        alertClass: 'alert-warning'
                    };
                }
                return {
                    html: 'La mission est prête à être lancée.',
                    alertClass: 'alert-success'
                };
            }

            else if (this.mission.status === 'ONGOING') {
                if (!this.draftEndComment) {
                    return {
                        html: 'Sélectionnez un <strong>commentaire de fin</strong> avant de terminer la mission.',
                        alertClass: 'alert-warning'
                    };
                }
                return {
                    html: 'Vous pourrez terminer la mission <strong>après avoir vérifié le mot de passe du chauffeur</strong>.',
                    alertClass: 'alert-success'
                };
            }

            return null;
        },
        /**
         * Tous les pré-checks du convoi sont-ils validés ?
         * (S’il n’y en a pas, on considère OK)
         */
        allPrechecksOk() {
            const vals = Array.isArray(this.mission?.missionPrecheckValues)
                ? this.mission.missionPrecheckValues
                : [];
            if (vals.length === 0) return true;
            return vals.every(v => v?.value === true);
        }
    },
    mounted() {
        this.refreshData();
        this.startMissionPolling();
    },
    beforeUnmount() {
        if (this.missionPollTimer) clearInterval(this.missionPollTimer);
        this.missionPollTimer = null;
    },
    methods: {
        async refreshData() {
            if (this.isRefreshingMission) return;
            this.isRefreshingMission = true;
            try {
                this.mission = await MissionRepository.getMission(this.$route.params.id);
                this.maybeRedirectToWizard();
            } catch (e) {
                console.log(e);
            } finally {
                this.isRefreshingMission = false;
            }
        },
        startMissionPolling() {
            if (this.missionPollTimer) return;
            // toutes les 5s : refresh mission complète (autres postes / autres onglets)
            this.missionPollTimer = setInterval(() => {
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
                this.refreshData();
            }, 5000);
        },
        async startMission(missionId) {
            if (!this.alertCode) {
                if (!Array.isArray(this.mission?.members) || this.mission.members.length === 0) {
                    this.$refs.panel?.focusNotification?.();
                    //alert("Impossible de démarrer : aucun chauffeur n’est assigné à cette mission.");
                    return;
                }
                if (!this.mission?.verifiedInfo === 'TWICE') {
                    this.$refs.panel?.focusNotification?.();
                    //alert("Impossible de démarrer : le chauffeur n'est pas vérifié. Veuillez saisir et valider le code du chauffeur.");
                    return;
                }
                if (!this.draftStartComment) {
                    this.$refs.panel?.focusNotification?.();
                    //alert("Sélectionnez un commentaire de départ avant de démarrer la mission.");
                    return;
                }
                const allPrechecksOk = this.mission.missionPrecheckValues.length === 0 ? true : this.mission.missionPrecheckValues.every(v => v?.value === true);

                if (!allPrechecksOk) {
                    const confirmLaunch = confirm(
                        "Lancer une mission sans que tous les tests soient validés présente des risques.\n" +
                        "Vous devrez rédiger un commentaire pour justifier ce choix.\n\n" +
                        "Souhaitez-vous lancer la mission quand même ?"
                    );
                    if (!confirmLaunch) {
                        return;
                    }
                }
            }
            MissionService.startMission(missionId, this.draftStartComment)
                .then(() => {
                    this.refreshData();
                    this.$refs.panel?.reloadComments?.();
                    this.$refs.launcher?.reloadComments?.();
                })
                .catch(e => console.log(e));
        },
        endMission(missionId) {
            if (!this.draftEndComment) {
                alert("Sélectionnez un commentaire de fin avant de terminer la mission.");
                return;
            }
            MissionService.endMission(missionId, this.draftEndComment)
                .then(() => {
                    this.refreshData();
                    this.$refs.panel?.reloadComments?.();
                    this.$refs.launcher?.reloadComments?.();
                })
                .catch(e => console.log(e));
        },
        transporteurUpdate(baliseId) {
            MissionService.transporteurUpdate(this.mission.id, { transporteurId: baliseId })
                .then(() => {
                    this.refreshData();
                })
                .catch(e => {
                    console.log(e);
                });
        },
        addPlate(immat) {
            MissionService.addMissionPlate(this.mission.id, immat).then(this.refreshData);
        },
        removePlate(plateId) {
            MissionService.removeMissionPlate(this.mission.id, plateId).then(this.refreshData);
        },
        onStartCommentChange(value) {
            this.draftStartComment = value;
        },
        onEndCommentChange(value) {
            this.draftEndComment = value;
        },
        async addMissionComment(content) {
            try {
                await MissionService.createMissionComment(this.mission.id, content);
                this.$refs.panel?.reloadComments?.();
                this.$refs.launcher?.reloadComments?.();
            } catch (e) {
                alert(e?.response?.data?.message || e.message || "Erreur lors de l’ajout du commentaire.");
            }
        },
        setAlertCode(value) {
            this.alertCode = value;
            this.draftStartComment = "Commentaire automatique après mot de passe sous contrainte.";
            this.refreshData();
        },
        async handleEndDraft() {
            try {
                await MissionService.endDraft(this.mission.id);
                this.refreshData();
            } catch (e) {
                console.log(e?.response?.data?.message || e.message || "Erreur.");
            }
        },
        /**
         * Redirige vers le wizard si des infos essentielles manquent.
         * On ne redirige que pour DRAFT/PENDING, pas pour les viewers,
         * et jamais si on est déjà dans le wizard.
         */
        maybeRedirectToWizard() {
            const m = this.mission;
            if (!m) return;
            // éviter les boucles si on est déjà dans le wizard
            const inWizard =
                (this.$route.path || '').includes('/setup') ||
                (this.$route.name || '').toString().startsWith('MissionWizard');
            if (inWizard) return;
            // rôles / statuts
            const editable = !this.$isViewer && (m.status === 'DRAFT' || m.status === 'PENDING');
            if (!editable) return;
            // critères de complétude
            const missingName = !(m.name && String(m.name).trim().length);
            const missingCorr = !m.withoutCorridoring && !(m.corridoringId || m.corridoring?.id);
            const missingTransporteur = !(m.transporteurId || m.transporteur?.id);
            const missingDriver = !(Array.isArray(m.members) && m.members.length > 0);
            if (missingName || missingCorr || missingTransporteur || missingDriver) {
                this.$router.replace({
                    name: 'MissionWizardName',
                    params: { id: m.id }
                });
            }
        },
    }
}
</script>